import type { Tenant } from '@console/types'
import styles from './TenantSwitcher.module.css'

export interface TenantSwitcherProps {
  tenants: Tenant[]
  /** How many rows Cadence left out. Reported, never inferred from the list. */
  excludedFixtures: number
  /** The tenant this session is working in — a Cadence UUID. */
  currentTenantId: string
  /** Called with the chosen workspace's UUID. */
  onSelect: (tenantId: string) => void
  /** True while the change is in flight — the control locks rather than queueing. */
  pending: boolean
  /** Set when the last change was refused, in Cadence's own words. */
  error: string | null
}

/**
 * THE EXCLUSION HAPPENS ON CADENCE'S SIDE NOW, and the count comes with it.
 *
 * This used to filter `origin !== 'fixture'` here. Cadence now excludes them
 * itself and reports `excluded_fixtures` on the envelope, which is better:
 * the registry decides what is in the registry, and the console is told how
 * many it did not send rather than inferring it from what arrived.
 *
 * The local filter is kept as a belt: an older Cadence, or one that stops
 * excluding, should not put fixture rows in front of an operator. It is
 * expected to match nothing.
 *
 * `development` is shown. It is not a customer — stamping it one would make
 * that word mean "a workspace someone uses" and miscount the first time
 * anything counted customers — but it is a workspace somebody deliberately
 * keeps with curated data, which is exactly what belongs in a switcher.
 */
function isWorthShowing(tenant: Tenant): boolean {
  return tenant.origin !== 'fixture'
}

/** Two fields, and they answer different questions — see the contract. */
function describe(tenant: Tenant): string {
  const operational = tenant.status === 'ACTIVE' ? null : tenant.status.toLowerCase()
  const onboarding = tenant.state === 'CONFIGURED' ? null : `${tenant.state.toLowerCase()} stage`
  const notes = [operational, onboarding].filter((n) => n !== null)
  return notes.length === 0 ? '' : ` — ${notes.join(', ')}`
}

/**
 * Which tenant's world is on screen, and which others exist.
 *
 * The UI spec said the tenant was "never a dropdown in v1". That was written
 * when the console served one tenant; it supervises many, so this is the
 * component the spec was missing rather than a departure from it.
 */
export function TenantSwitcher({
  tenants,
  excludedFixtures,
  currentTenantId,
  onSelect,
  pending,
  error,
}: TenantSwitcherProps) {
  const visible = tenants.filter(isWorthShowing)
  // Cadence's count plus anything the local belt caught. Both, because a row
  // that arrived AND was filtered here is one Cadence did not exclude.
  const hidden = excludedFixtures + (tenants.length - visible.length)
  const current = tenants.find((t) => t.id === currentTenantId)

  return (
    <div className={styles['switcher']}>
      <label className={styles['label']} htmlFor="tenant-switcher">
        Workspace
      </label>
      <select
        id="tenant-switcher"
        className={styles['select']}
        value={currentTenantId}
        // Locked while a change is in flight, so a second selection cannot
        // race the first and leave the screen showing a workspace nobody
        // picked.
        disabled={pending}
        onChange={(event) => {
          onSelect(event.target.value)
        }}
      >
        {/*
          The current tenant may legitimately be absent from the list: it is
          whatever Cadence reports serving, and the registry could have moved
          on. Rendering it anyway is more honest than showing a blank select
          that implies no selection at all.
        */}
        {current === undefined && (
          <option value={currentTenantId}>
            Unlisted workspace ({currentTenantId.slice(0, 8)}…)
          </option>
        )}
        {visible.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.displayName}
            {describe(tenant)}
          </option>
        ))}
      </select>
      {hidden > 0 && (
        <p className={styles['reason']}>
          {hidden} test {hidden === 1 ? 'workspace' : 'workspaces'} excluded — declared as fixtures
          by whatever created them, not guessed from the name.
        </p>
      )}
      {pending && <p className={styles['reason']}>Switching workspace…</p>}
      {error !== null && (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
